import { cleanLibrary, cleanSpec, layoutName, cloneLayout } from './layouts'

export const emptyCatalog = () => ({ version: 1, layouts: [] })
const userId = value => value == null ? '' : String(value)
export function cleanAccess(raw) {
  const seen = new Set()
  return (Array.isArray(raw) ? raw : []).filter(item => item && userId(item.userId) && !seen.has(userId(item.userId)) && seen.add(userId(item.userId)))
    .slice(0, 5000).map(item => ({ userId: userId(item.userId), favorite: !!item.favorite || !!item.locked, locked: !!item.locked }))
}
export function cleanCatalog(raw) {
  if (!raw || raw.version !== 1 || !Array.isArray(raw.layouts)) throw Error('Catálogo de layouts inválido.')
  const seen = new Set()
  return { version: 1, layouts: raw.layouts.slice(0, 200).filter(item => item && typeof item.id === 'string' && item.id.length <= 100 && !['system', 'custom'].includes(item.id) && layoutName(item.name) && !seen.has(item.id) && seen.add(item.id))
    .map(item => ({ id: item.id, name: layoutName(item.name), spec: cleanSpec(item.spec), ownerId: userId(item.ownerId), users: cleanAccess(item.users) })) }
}
export function catalogKey({ system, company, grid }) {
  return 'escalador:grid-layout-access:v1:' + [system, company ?? 'geral', grid].map(value => encodeURIComponent(String(value))).join(':')
}
export function readCatalog(storage, key) {
  let raw = null
  try {
    raw = storage.getItem(key)
    if (raw == null) return { catalog: emptyCatalog(), raw, warning: '' }
    if (raw.length > 2097152) throw Error('Catálogo muito extenso.')
    return { catalog: cleanCatalog(JSON.parse(raw)), raw, warning: '' }
  } catch { return { catalog: emptyCatalog(), raw, warning: 'Não foi possível carregar as regras de acesso deste navegador. Recarregue a configuração antes de alterá-las.', invalid: true } }
}
export function userLayoutPolicy(catalog, id) {
  const linked = catalog.layouts.filter(layout => layout.users.some(access => access.userId === userId(id)))
  const locked = linked.find(layout => layout.users.some(access => access.userId === userId(id) && access.locked))
  const favorite = linked.find(layout => layout.users.some(access => access.userId === userId(id) && access.favorite))
  return { lockedId: locked?.id || null, favoriteId: locked?.id || favorite?.id || null }
}
export function combinedLibrary(personal, catalog, id, manager, preferredActive) {
  const policy = userLayoutPolicy(catalog, id)
  const shared = catalog.layouts.filter(layout => manager || layout.users.some(access => access.userId === userId(id)))
    .map(layout => ({ ...cloneLayout(layout), shared: true }))
  const sharedIds = new Set(catalog.layouts.map(layout => layout.id))
  const own = personal.layouts.filter(layout => !sharedIds.has(layout.id)).map(layout => ({ ...cloneLayout(layout), ownerId: userId(id), shared: false, users: [] }))
  const layouts = [...own, ...shared]
  const allowed = new Set(['system', ...(personal.custom ? ['custom'] : []), ...layouts.map(layout => layout.id)])
  const favorite = allowed.has(personal.sharedFavorite) ? personal.sharedFavorite : allowed.has(personal.favorite) ? personal.favorite : null
  const previous = allowed.has(personal.sharedActive) ? personal.sharedActive : personal.active
  const active = policy.lockedId || (allowed.has(preferredActive) ? preferredActive : policy.favoriteId || favorite || (allowed.has(previous) ? previous : 'system'))
  return { ...cloneLayout(personal), active, favorite, layouts, policy }
}
export function changeAccess(library, layoutId, targetUser, kind, checked) {
  const result = cloneLayout(library), id = userId(targetUser), layout = result.layouts.find(item => item.id === layoutId)
  if (!layout || !id) throw Error('Selecione um layout e um usuário válidos.')
  if (kind === 'access' && !checked) { layout.users = (layout.users || []).filter(item => item.userId !== id); return result }
  if (!['access', 'favorite', 'locked'].includes(kind)) throw Error('Opção de acesso inválida.')
  layout.shared = true; layout.users = cleanAccess(layout.users)
  let access = layout.users.find(item => item.userId === id)
  if (!access && kind !== 'access') throw Error('Marque o acesso do usuário antes de definir favorito ou bloqueio.')
  if (!access && layout.users.length >= 5000) throw Error('Limite de 5000 usuários por layout.')
  if (!access) { access = { userId: id, favorite: false, locked: false }; layout.users.push(access) }
  if (kind === 'access') return result
  if (kind === 'favorite' && !checked && access.locked) throw Error('Um layout bloqueado também é o padrão do usuário.')
  if (kind === 'favorite' && checked && result.layouts.some(item => item.id !== layoutId && item.users?.some(a => a.userId === id && a.locked))) throw Error('Desbloqueie o outro layout desse usuário antes de alterar o favorito.')
  if (checked) for (const item of result.layouts) for (const rule of item.users || []) if (rule.userId === id) {
    rule.favorite = false
    if (kind === 'locked') rule.locked = false
  }
  access[kind] = checked
  if (kind === 'locked') access.favorite = checked
  return result
}

export function markAllAccess(library, layoutId, ids) {
  const result = cloneLayout(library), layout = result.layouts.find(item => item.id === layoutId)
  if (!layout) throw Error('Selecione um layout válido.')
  const users = new Map(cleanAccess(layout.users).map(rule => [rule.userId, rule]))
  for (const value of ids || []) {
    const id = userId(value)
    if (id && !users.has(id)) users.set(id, { userId: id, favorite: false, locked: false })
  }
  if (users.size > 5000) throw Error('Limite de 5000 usuários por layout.')
  layout.shared = true; layout.users = [...users.values()]
  return result
}

// Valida novamente no consumidor: o componente visual não concede privilégios.
export function prepareLayoutSave(working, personal, catalog, id, manager) {
  const policy = userLayoutPolicy(catalog, id), current = combinedLibrary(personal, catalog, id, manager)
  if (!manager && policy.lockedId && working.active !== policy.lockedId) throw Error('O administrador fixou um layout para este usuário. Não é possível trocar.')
  const originals = new Map(catalog.layouts.map(item => [item.id, item]))
  if (!manager) {
    for (const layout of working.layouts || []) {
      const original = originals.get(layout.id)
      if (original) {
        if (!current.layouts.some(item => item.id === layout.id) || JSON.stringify({ id: layout.id, name: layout.name, spec: cleanSpec(layout.spec), ownerId: layout.ownerId, users: cleanAccess(layout.users) }) !== JSON.stringify(original)) throw Error('Somente o administrador pode alterar um layout compartilhado.')
      } else if (layout.shared || layout.users?.length) throw Error('Somente o administrador pode definir acesso a layouts.')
    }
    if (policy.lockedId && (JSON.stringify(working.layouts) !== JSON.stringify(current.layouts) || working.favorite !== current.favorite || JSON.stringify(working.custom) !== JSON.stringify(personal.custom))) throw Error('O layout fixado não pode ser personalizado por este usuário.')
  }
  const ids = new Set((working.layouts || []).map(item => item.id))
  if (!['system', 'custom'].includes(working.active) && !ids.has(working.active)) throw Error('O layout selecionado não está disponível.')
  const published = working.layouts.filter(item => item.shared || item.users?.length)
  if (manager && published.length > 200) throw Error('Limite de 200 layouts compartilhados nesta grid. Remova um layout antes de publicar outro.')
  const shared = manager ? cleanCatalog({ version: 1, layouts: published.map(item => ({ ...item, ownerId: item.ownerId || userId(id) })) }) : catalog
  for (const layout of shared.layouts) for (const rule of layout.users) {
    if (rule.locked && !rule.favorite) throw Error('Layout bloqueado deve ser o padrão do usuário.')
    if (rule.favorite && shared.layouts.some(other => other.id !== layout.id && other.users.some(a => a.userId === rule.userId && a.favorite))) throw Error('Cada usuário deve ter apenas um layout favorito.')
    if (rule.locked && shared.layouts.some(other => other.id !== layout.id && other.users.some(a => a.userId === rule.userId && a.locked))) throw Error('Cada usuário deve ter apenas um layout bloqueado.')
  }
  const privateLayouts = working.layouts.filter(item => !originals.has(item.id) && !item.shared && !item.users?.length)
  const next = cleanLibrary({ ...working, layouts: privateLayouts })
  // A seleção/favorito pode apontar para o catálogo compartilhado, não só aos pessoais.
  const sharedIds = new Set(shared.layouts.map(layout => layout.id))
  next.sharedActive = sharedIds.has(working.active) ? working.active : null
  next.sharedFavorite = sharedIds.has(working.favorite) ? working.favorite : null
  if (next.sharedActive) next.active = 'system'
  if (next.sharedFavorite) next.favorite = null
  return { personal: next, catalog: shared }
}

export function commitLayoutStorage(storage, entries) {
  for (const entry of entries) if (entry.key && storage.getItem(entry.key) !== entry.before) throw Error('Os layouts mudaram em outra aba. Feche e abra a configuração novamente.')
  const written = []
  try {
    for (const entry of entries) if (entry.key && entry.value !== entry.before) { storage.setItem(entry.key, entry.value); written.push(entry) }
  } catch (error) {
    for (const entry of written.reverse()) try {
      if (storage.getItem(entry.key) === entry.value) {
        if (entry.before == null) storage.removeItem(entry.key)
        else storage.setItem(entry.key, entry.before)
      }
    } catch { /* A próxima leitura precisa conciliar a versão efetivamente disponível. */ }
    throw error
  }
}
